import type { FormHelperTextProps } from '@mui/material';

import { useState } from 'react';
import { MenuItem, Stack } from '@mui/material';

import { TextField } from '@theguarantors/tg-package-material-ui';

import { useScreenWidth } from '@/hooks';
import { createDataTestId } from '@/utils/createDataTestId';
import { FieldHelperText, fieldHelperTextSx } from '../../components/field-helper.text';
import { useLeaseField } from '../../hooks/use-lease-field';
import { useLeaseDetailsUI } from '../../lease-details-ui.context';

type BaseSelectOption = {
  id: string;
  value: string;
  label: string;
};

export type LeaseDetailsSelectProps<T extends BaseSelectOption> = {
  name: string;
  title: string;
  helperText?: React.ReactNode;
  options: T[];
  defaultValue?: string;
  onClose?: () => void;
  SelectItem: React.ComponentType<SelectItemProps<T>>;
  required?: boolean;
  onSelect?: (value: string) => void;
  disabled?: boolean;
  emptyOption: T;
};

type SelectItemProps<T> = {
  option: T;
  open: boolean;
};

const getMenuProps = (isDesktop: boolean) => {
  if (isDesktop) {
    return {};
  }

  return {
    anchorOrigin: {
      horizontal: 'center',
      vertical: 'top',
    },
    transformOrigin: {
      horizontal: 'center',
      vertical: 'bottom',
    },
  } as const;
};

export function LeaseDetailsSelect<T extends BaseSelectOption>({
  name,
  title,
  helperText,
  options,
  defaultValue = '',
  onClose,
  SelectItem,
  required = false,
  onSelect,
  disabled = false,
  emptyOption,
}: LeaseDetailsSelectProps<T>) {
  const field = useLeaseField<string | null | undefined>(name);
  const { isDesktop } = useScreenWidth();
  const { inputSize } = useLeaseDetailsUI();
  const [isSelecting, setIsSelecting] = useState(false);

  const value = field.value || defaultValue;
  const error = field.error;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: nextValue } = event?.target ?? {};
    if (nextValue == null) {
      return;
    }

    onSelect?.(nextValue);
    field.onChange(nextValue);
  };

  const handleClose = () => {
    setIsSelecting(false);
    field.onBlur();
    onClose?.();
  };

  const handleOpen = () => {
    setIsSelecting(true);
  };

  const menuProps = getMenuProps(isDesktop);

  const dataTestId = createDataTestId({ inputString: name, suffix: 'select' });

  return (
    <Stack direction="column" spacing={1}>
      <TextField
        id={dataTestId}
        data-testid={dataTestId}
        inputRef={field.ref}
        color="secondary"
        disabled={disabled}
        error={Boolean(error?.message)}
        helperText={error?.message?.toString()}
        label={title}
        name={name}
        onChange={handleChange}
        select={true}
        required={required}
        size={inputSize}
        FormHelperTextProps={
          {
            'data-testid': `${dataTestId}-error-text`,
            sx: fieldHelperTextSx,
          } as FormHelperTextProps & { 'data-testid': string }
        }
        slotProps={{
          inputLabel: disabled ? { shrink: false } : undefined,
          select: {
            onClose: handleClose,
            onOpen: handleOpen,
            required,
            renderValue: (selected: unknown) => {
              if (disabled) {
                return '';
              }

              const selectedOption = options.find(({ value }) => value === selected);
              const currentlySelected = selectedOption || emptyOption;

              if (isSelecting) {
                return currentlySelected.label;
              }

              return <SelectItem option={currentlySelected} open={false} />;
            },
            MenuProps: {
              ...menuProps,
              slotProps: {
                backdrop: {
                  invisible: true,
                  sx: {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
          },
        }}
        sx={{ width: '100%' }}
        value={value}
      >
        {options.map((option) => (
          <MenuItem
            key={option.id}
            title={option.label}
            value={option.value}
            sx={{
              padding: '8px 12px',
            }}
          >
            <SelectItem option={option} open={isSelecting} />
          </MenuItem>
        ))}
      </TextField>
      {/* Suppress while an inline error is showing — error and supporting copy
          never render at the same time. */}
      {helperText && !error?.message ? (
        <FieldHelperText
          text={helperText}
          disabled={disabled}
          dataTestId={`${dataTestId}-helper-text`}
        />
      ) : null}
    </Stack>
  );
}
